import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatProductPreview, ChatSalesContact, ChatService } from './chat-bot.service';

interface ChatMessage {
  role: string;
  content: string;
  products?: ChatProductPreview[];
  salesContacts?: ChatSalesContact[];
}

@Component({
  selector: 'app-chat-bot',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-bot.html',
  styleUrl: './chat-bot.css',
})
export class ChatBot implements AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLDivElement>;
  @Input() showLauncher = true;

  userInput = '';
  chatHistory: ChatMessage[] = [];
  isLoading = false;
  isOpen = false;
  private shouldScrollToBottom = false;
  private sessionId: string;

  constructor(private chatService: ChatService, private changeDetector: ChangeDetectorRef) {
    this.sessionId = this.chatService.getSessionId();
  }

  ngAfterViewChecked() {
    if (!this.shouldScrollToBottom) return;

    this.scrollToBottom();
    this.shouldScrollToBottom = false;
  }

  toggleChat() {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.queueScrollToBottom();
    }
  }

  openChat() {
    this.isOpen = true;
    this.queueScrollToBottom();
  }

  closeChat() {
    this.isOpen = false;
  }

  resetChat() {
    this.chatHistory = [];
    this.userInput = '';
    this.sessionId = this.chatService.resetSession();
    this.queueScrollToBottom();
  }

  send() {
    const textToSend = this.userInput.trim();

    if (!textToSend || this.isLoading) return;

    this.chatHistory.push({ role: 'User', content: textToSend });
    this.userInput = '';
    this.isLoading = true;
    this.queueScrollToBottom();

    this.chatService.sendMessage(textToSend, this.sessionId).subscribe({
      next: (res) => {
        this.sessionId = res.sessionId;
        this.isLoading = false;
        this.chatHistory = [
          ...this.chatHistory,
          {
            role: 'Support',
            content: res.message,
            products: res.products ?? [],
            salesContacts: res.salesContacts ?? [],
          },
        ];
        this.flushChatView();
      },
      error: (err) => {
        console.error('Chatbot request failed:', err);
        this.isLoading = false;
        this.chatHistory = [
          ...this.chatHistory,
          { role: 'Sistem', content: 'Chatbot sedang tidak tersedia. Silakan coba kembali.' },
        ];
        this.flushChatView();
      },
    });
  }

  onMessageKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    this.send();
  }

  private queueScrollToBottom() {
    this.shouldScrollToBottom = true;
  }

  private flushChatView() {
    this.queueScrollToBottom();
    this.changeDetector.detectChanges();
    window.setTimeout(() => this.scrollToBottom(), 0);
  }

  private scrollToBottom() {
    const element = this.messagesContainer?.nativeElement;

    if (!element) return;

    element.scrollTop = element.scrollHeight;
  }
}
